import * as THREE from 'three';

const PARTICLE_COUNT = 24;
const LIFETIME = 0.7; // seconds
const GRAVITY = 3.2;

interface ActiveBurst {
  elapsed: number;
  velocities: Float32Array;
}

/** One-shot particle pop triggered per answer — sparkles up for correct, dust puffs out for incorrect. */
export class BurstParticles {
  readonly points: THREE.Points;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsMaterial;
  private active: ActiveBurst | null = null;

  constructor() {
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(PARTICLE_COUNT * 3), 3));
    this.material = new THREE.PointsMaterial({
      size: 0.16,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.geometry.setDrawRange(0, 0);
  }

  /** @param origin world-space spawn point (e.g. the trash pile) */
  trigger(origin: THREE.Vector3, correct: boolean): void {
    const positions = this.geometry.attributes.position.array as Float32Array;
    const velocities = new Float32Array(PARTICLE_COUNT * 3);

    for (let i = 0; i < PARTICLE_COUNT; i += 1) {
      positions[i * 3] = origin.x;
      positions[i * 3 + 1] = origin.y + 0.2;
      positions[i * 3 + 2] = origin.z;

      const angle = Math.random() * Math.PI * 2;
      const speed = correct ? 1.4 + Math.random() * 1.6 : 0.8 + Math.random() * 1.2;
      velocities[i * 3] = Math.cos(angle) * speed;
      velocities[i * 3 + 1] = correct ? 2.2 + Math.random() * 1.6 : 0.5 + Math.random() * 0.6;
      velocities[i * 3 + 2] = Math.sin(angle) * speed;
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.setDrawRange(0, PARTICLE_COUNT);
    this.material.color.set(correct ? '#8ff0ab' : '#d9634a');
    this.material.size = correct ? 0.16 : 0.24;
    this.active = { elapsed: 0, velocities };
  }

  update(delta: number): void {
    if (!this.active) return;

    this.active.elapsed += delta;
    const t = this.active.elapsed / LIFETIME;
    if (t >= 1) {
      this.geometry.setDrawRange(0, 0);
      this.material.opacity = 0;
      this.active = null;
      return;
    }

    const positions = this.geometry.attributes.position.array as Float32Array;
    const velocities = this.active.velocities;
    for (let i = 0; i < PARTICLE_COUNT; i += 1) {
      positions[i * 3] += velocities[i * 3] * delta;
      positions[i * 3 + 1] += velocities[i * 3 + 1] * delta;
      positions[i * 3 + 2] += velocities[i * 3 + 2] * delta;
      velocities[i * 3 + 1] -= GRAVITY * delta;
    }
    this.geometry.attributes.position.needsUpdate = true;
    this.material.opacity = 0.9 * (1 - t);
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
